import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Building2, CheckCircle2 } from "lucide-react";

export default function RegisterCompany() {
  const [form, setForm] = useState({
    company_name: "", trade: "",
    admin_first_name: "", admin_last_name: "",
    admin_email: "", admin_password: "",
  });
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { registerCompany } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/meta/trades").then(r => setTrades(r.data.trades || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const first = form.admin_first_name.trim();
    const last = form.admin_last_name.trim();
    if (!first || !last) {
      toast.error("Admin first and last name are required");
      return;
    }
    setLoading(true);
    try {
      // Backend expects `admin_name` (single string); compose from First + Last
      const data = await registerCompany({
        company_name: form.company_name,
        trade: form.trade,
        admin_name: `${first} ${last}`,
        admin_first_name: first,
        admin_last_name: last,
        admin_email: form.admin_email,
        admin_password: form.admin_password,
      });
      setResult(data);
      toast.success("Company registered!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4 grid-bg">
        <div className="brutal-card p-8 max-w-xl w-full">
          <CheckCircle2 className="w-14 h-14 text-status-current mb-4" strokeWidth={2.5} />
          <div className="font-heading text-4xl mb-2">YOU&apos;RE IN.</div>
          <p className="text-muted-foreground mb-6">Save your company access code — share it with employees and supervisors so they can log in.</p>
          <div className="border-2 border-safety-yellow bg-safety-yellow/10 p-4 mb-6">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Access Code</div>
            <div className="font-heading text-4xl text-safety-yellow" data-testid="new-access-code">{result.company.access_code}</div>
          </div>
          <button onClick={() => navigate("/dashboard")} data-testid="go-to-dashboard" className="btn-primary w-full">
            Enter Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-4 md:p-10 grid-bg">
      <div className="max-w-2xl mx-auto">
        <Link to="/signup" data-testid="register-back" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-6 hover:text-safety-yellow">
          <ArrowLeft className="w-4 h-4" /> Back to choice
        </Link>
        <div className="brutal-card p-6 md:p-10">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-8 h-8 text-safety-yellow" strokeWidth={2.5} />
            <div className="font-heading text-3xl md:text-4xl">REGISTER COMPANY</div>
          </div>
          <p className="text-sm text-muted-foreground mb-8">Create an admin account and get a shareable access code for your crew.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block font-bold uppercase text-xs tracking-wider mb-2">Company Name</label>
              <input data-testid="reg-company-name" required className="industrial-input"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            </div>
            <div>
              <label className="block font-bold uppercase text-xs tracking-wider mb-2">Primary Trade</label>
              <select data-testid="reg-trade" required className="industrial-input"
                value={form.trade}
                onChange={(e) => setForm({ ...form, trade: e.target.value })}>
                <option value="">Select a trade...</option>
                {trades.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block font-bold uppercase text-xs tracking-wider mb-2">First Name</label>
                <input data-testid="reg-admin-first-name" required className="industrial-input"
                  value={form.admin_first_name}
                  onChange={(e) => setForm({ ...form, admin_first_name: e.target.value })}
                  autoComplete="given-name" />
              </div>
              <div>
                <label className="block font-bold uppercase text-xs tracking-wider mb-2">Last Name</label>
                <input data-testid="reg-admin-last-name" required className="industrial-input"
                  value={form.admin_last_name}
                  onChange={(e) => setForm({ ...form, admin_last_name: e.target.value })}
                  autoComplete="family-name" />
              </div>
            </div>
            <div>
              <label className="block font-bold uppercase text-xs tracking-wider mb-2">Admin Email</label>
              <input data-testid="reg-admin-email" required type="email" className="industrial-input"
                value={form.admin_email}
                onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                autoComplete="email" />
            </div>
            <div>
              <label className="block font-bold uppercase text-xs tracking-wider mb-2">Password (min 8 chars)</label>
              <input data-testid="reg-admin-password" required type="password" minLength={8} className="industrial-input"
                value={form.admin_password}
                onChange={(e) => setForm({ ...form, admin_password: e.target.value })} />
            </div>
            <button data-testid="reg-submit" disabled={loading} type="submit" className="btn-primary w-full">
              {loading ? "Creating..." : "Create Company"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
